import { Component, OnDestroy, ElementRef, OnInit, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy, NgZone } from '@angular/core';
import { Chart, registerables, Tooltip, TooltipPositionerFunction, ChartType } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Subscription } from 'rxjs';
import { DashboardService } from '../../../services/dashboard.service';
import { GlobalsService } from '../../../services/globals.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

declare module 'chart.js' {
	interface TooltipPositionerMap {
		top: TooltipPositionerFunction<ChartType>;
	}
}

@Component({
	selector: 'app-dashboard-sales-this-last',
	templateUrl: './dashboard-sales-this-last.component.html',
	styleUrls: ['./dashboard-sales-this-last.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardSalesThisLastComponent implements OnInit, OnDestroy {
	@ViewChild('monthView') monthViewRef: ElementRef;
	datepicked: any = false;
	thisoverlast: any = false;
	lineover: any = false;
	dayInvoices: any = false;
	displaydetails: any = false;
	overLastStatus: { loading: boolean; day: number; monthname: string; monthnum: number; fromyear: string; toyear: string; viewing: string; source: number; message: string; temp: { year: any; month: any; }; } = {
		loading: false,
		day: 0,
		monthname: '',
		monthnum: 0,
		fromyear: '',
		toyear: '',
		viewing: 'year',
		source: 2,
		message: '',
		temp: { year: '', month: [] }
	};
	viewingInvoice: any = false;
	fetching_day: Subscription;
	fetching_month: Subscription;
	private yearSub: Subscription;
	private configSub: Subscription;
	private userSub: Subscription;
	user: any = null;

	constructor(
		private dashboardService: DashboardService,
		private globalsService: GlobalsService,
		private globalSearchService: GlobalSearchService,
		private modalService: NgbModal,
		private cdr: ChangeDetectorRef,
		private ngZone: NgZone
	) {
		Tooltip.positioners.top = function (items) {
			const chart = this.chart;
			return {
				x: chart.chartArea.right,
				y: chart.chartArea.top,
				xAlign: 'center',
				yAlign: 'bottom'
			};
		};
		this.configSub = this.globalSearchService.configsubscription.subscribe(r => {
			if (r) {
				this.loadData();
			}
		});
		this.userSub = this.globalSearchService.user.subscribe(user => {
			this.user = user;
			this.cdr.markForCheck();
		});
	}

	ngOnInit() {
		Chart.register(...registerables);
		Chart.register(ChartDataLabels);
		this.defaultThisOverLast();
	}

	ngOnDestroy() {
		this.configSub?.unsubscribe();
		this.yearSub?.unsubscribe();
		this.userSub?.unsubscribe();
		this.fetching_month?.unsubscribe();
		this.fetching_day?.unsubscribe();
		if (this.lineover && typeof this.lineover.destroy === 'function') {
			this.lineover.destroy();
		}
	}

	loadData() {
		if (!this.overLastStatus) {
			this.defaultThisOverLast();
		}
		this.overLastStatus.loading = true;
		this.cdr.markForCheck();
		this.yearSub = this.dashboardService.getThisYearOverLast({}).subscribe((results: any) => {
			this.overLastStatus.temp.year = results;
			this.overLastStatus.loading = false;
			this.thisoverlast = results;
			const monthlables = results.label_sets[0];
			const datasets = results.data_sets;
			this.ngZone.run(() => {
				setTimeout(() => {
					if (this.lineover && typeof this.lineover.destroy === 'function') {
						this.lineover.destroy();
					}
					this.createYearOverLast(monthlables, datasets);
					this.cdr.markForCheck();
				}, 0);
			});
			this.cdr.markForCheck();
		});
	}

	defaultThisOverLast() {
		let prev_data: any;
		if (this.overLastStatus) {
			prev_data = this.overLastStatus.temp;
		}
		const temp = prev_data ? prev_data : { year: '', month: [] };
		this.overLastStatus = {
			loading: false,
			day: 0,
			monthname: '',
			monthnum: 0,
			fromyear: '',
			toyear: '',
			viewing: 'year',
			source: 2,
			message: '',
			temp: temp
		};
	}

	getMonthYearQty(month: any, year: any) {
		const obj = year.qty.filter((monthyear: any) => monthyear.label === month)[0];
		let color = '';
		if (obj.monthgrowth !== -1) {
			color = (obj.monthgrowth > 0) ? 'text-success' : 'text-danger';
		}
		let mcolor = '';
		if (obj.monthpreviousgrowth !== 0 && obj.monthpreviousgrowth !== -1) {
			mcolor = (obj.monthpreviousgrowth > 0) ? 'text-success' : 'text-danger';
		}
		return {
			percent_month: obj.monthpreviousgrowth,
			percent_month_color: mcolor,
			percent_month_icon: (obj.monthpreviousgrowth > 0) ? 'fa-long-arrow-up' : 'fa-long-arrow-down',
			percent: (obj.monthgrowth === -1) ? 0 : obj.monthgrowth,
			class: color,
			icon: (obj.monthgrowth > 0) ? 'fa-long-arrow-up' : 'fa-long-arrow-down',
		};
	}

	getSaleTotalYear(year: any) {
		if (!year) return 0;
		return year.sales.reduce((acc: number, items: any) => acc + parseFloat(items.value), 0);
	}

	getCostTotalYear(year: any) {
		return year.cost.reduce((acc: number, items: any) => acc + parseFloat(items.value), 0);
	}

	getQtyTotalYear(year: any) {
		return year.qty.reduce((acc: number, items: any) => acc + parseFloat(items.value), 0);
	}

	viewHistory(month: any, year: any) {
		if (parseInt(month, 10) < 10) {
			month = '0' + month;
		}
		this.datepicked = new Date(year + '-' + month + '-15');
		this.modalService.open(this.monthViewRef, { ariaLabelledBy: 'modal-title', size: 'xl', animation: false }).result.then(() => {}, () => {});
		this.cdr.markForCheck();
	}

	getDayByDay(chart: any) {
		this.fetching_month?.unsubscribe();
		const data = {
			monthnum: chart['tooltip']['dataPoints'][0].dataIndex + 1,
			fromyear: chart['legend']['legendItems'][0]['text'][0],
			toyear: chart['legend']['legendItems'][chart.legend.legendItems.length - 1]['text'][0]
		};
		this.overLastStatus.monthname = (new Date(0, data.monthnum, 0)).toLocaleString('default', { month: 'long' });
		this.overLastStatus.monthnum = data.monthnum;
		this.overLastStatus.message = `Loading ${this.overLastStatus.monthname}...`;
		this.overLastStatus.loading = true;
		this.cdr.markForCheck();
		this.fetching_month = this.dashboardService.getThisMonthOverLast(data).subscribe((results: any) => {
			this.overLastStatus.temp.month[0] = results;
			this.overLastStatus.loading = false;
			this.overLastStatus.viewing = 'month';
			this.thisoverlast = results;
			if (this.lineover && typeof this.lineover.destroy === 'function') {
				this.lineover.destroy();
			}
			const monthlables = results.label_sets[0];
			const datasets = results.data_sets;
			this.createMonthOverLast(monthlables, datasets);
			this.cdr.markForCheck();
		});
	}

	getDaySales(event: any, elements: any, chart: any) {
		if (this.fetching_day) {
			this.fetching_day.unsubscribe();
			this.overLastStatus.loading = false;
			this.overLastStatus.message = '';
		}
		this.overLastStatus.temp.month[1] = JSON.parse(JSON.stringify(this.overLastStatus));
		if (chart !== '') {
			this.overLastStatus.fromyear = chart['legend']['legendItems'][0]['text'][0];
			this.overLastStatus.toyear = chart['legend']['legendItems'][chart.legend.legendItems.length - 1]['text'][0];
			this.overLastStatus.monthnum = new Date(Date.parse(`${this.overLastStatus.monthname} 1,2013`)).getMonth() + 1;
			this.overLastStatus.day = elements[0].index + 1;
		} else {
			this.overLastStatus.fromyear = elements;
			this.overLastStatus.toyear = elements;
			this.overLastStatus.monthnum = new Date(Date.parse(`${this.overLastStatus.monthname} 1,2013`)).getMonth() + 1;
			this.overLastStatus.day = event;
		}
		this.overLastStatus.message = `Loading ${this.overLastStatus.monthname} ${this.overLastStatus.day}, ${this.overLastStatus.fromyear === this.overLastStatus.toyear ? this.overLastStatus.fromyear : `${this.overLastStatus.fromyear} - ${this.overLastStatus.toyear}`}`;
		this.overLastStatus.loading = true;
		this.cdr.markForCheck();
		this.fetching_day = this.dashboardService.getThisDayOverLast(this.overLastStatus).subscribe((results: any) => {
			this.openDocument(results[0].orderno, results[0].type, results[0].id, 'details', 'pdf');
			this.dayInvoices = results;
			this.overLastStatus.loading = false;
			this.overLastStatus.viewing = 'day';
			this.cdr.markForCheck();
		});
	}

	overLastBack() {
		const viewing = this.overLastStatus.viewing;
		switch (viewing) {
			case 'month':
				this.defaultThisOverLast();
				this.thisoverlast = this.overLastStatus.temp.year;
				break;
			case 'day':
				this.thisoverlast = this.overLastStatus.temp.month[0];
				this.overLastStatus = this.overLastStatus.temp.month[1];
				this.dayInvoices = false;
				this.viewingInvoice = false;
				break;
		}
		const monthlables = this.thisoverlast.label_sets[0];
		const datasets = this.thisoverlast.data_sets;
		if (this.lineover && typeof this.lineover.destroy === 'function') {
			this.lineover.destroy();
		}
		switch (viewing) {
			case 'month':
				this.createYearOverLast(monthlables, datasets);
				break;
			case 'day':
				this.createMonthOverLast(monthlables, datasets);
				break;
		}
		this.cdr.markForCheck();
	}

	openDocument(orderno: any, type: any, id: any, content: any, display: string) {
		switch (type) {
			case '11':
				this.globalsService.getCreditNote(id, display).subscribe((result: any) => {
					this.displaydetails = result;
					this.cdr.markForCheck();
				});
				break;
			case '10':
				this.globalsService.getInvoice(id, display).subscribe((result: any) => {
					this.displaydetails = result;
					this.cdr.markForCheck();
				});
				break;
		}
		this.viewingInvoice = { id: id, orderno: orderno };
		this.cdr.markForCheck();
	}

	createYearOverLast(monthlables: any, datasets: any) {
		this.lineover = new Chart('thisYoy', {
			type: 'bar',
			data: { labels: monthlables, datasets },
			options: {
				responsive: true,
				onClick: (e: any) => this.getDayByDay(e['chart']),
				interaction: { mode: 'index' },
				plugins: {
					legend: { position: 'bottom' },
					title: { display: false, text: 'YoY Sales' },
					autocolors: { enabled: false, offset: 5, repeat: 2 }
				}
			}
		});
	}

	createMonthOverLast(monthlables: any, datasets: any) {
		this.lineover = new Chart('thisYoy', {
			type: 'bar',
			data: { labels: monthlables, datasets },
			options: {
				responsive: true,
				scales: { x: { stacked: true }, y: { stacked: true } },
				onClick: (e: any, context: any, chart: any) => this.getDaySales(e, context, chart),
				interaction: { mode: 'index' },
				plugins: {
					datalabels: {
						listeners: { enter: () => true, leave: () => false, click: () => {} },
						display: (context: any) => context.active && context.dataset['data'][context.dataIndex] !== 0,
						formatter: (value: any, context: any) => `${context.dataset.label} : $${value}`,
						rotation: () => 0,
						align: 'center',
						borderWidth: 1,
						borderRadius: 5,
						borderColor: 'black',
						backgroundColor: (context: any) => context.active ? context.dataset.backgroundColor?.valueOf().toString().replace('.5', '1') : undefined,
						color: 'black',
						opacity: 90,
					},
					legend: { position: 'bottom' },
					title: { display: false, text: 'YoY Sales' },
					autocolors: { enabled: false, offset: 5, repeat: 2 },
					tooltip: { position: 'top' }
				}
			}
		});
	}
}
